// THE BROADCAST TOOL — THE LAW. Her order, 2026-08-11 (queue item 4).
//
// **HER ORDER:** a new owner-only dashboard section, **Broadcasts**. Audiences selectable per send
// (ALL, PAID, SIGNED UP BUT NEVER PAID, and per-tier slices), **counts shown live before sending**,
// **test-flagged seats and owner seats never receive**. One composer, two channels (Push, Email or
// both). Push follows the **three-line law** and the **no-brand-echo law**. Email is subject plus a
// simple rich body on her Gmail rail with the **required unsubscribe link, unsubscribes honored
// automatically and tracked, transactional mail unaffected**. A **preview screen**, the audience
// count in plain numbers, and a **typed confirmation (`SEND TO N PEOPLE`)** before anything fires.
// The send is **detached, batched, ledgered**, with progress visible and viewable history.
//
// **THIS FILE IS PURE AND IMPORTS ONLY `./notifications.js`**, which is itself the shared push law —
// so a FAST-tier fixture LOADS AND RUNS every decision in here (R5/R8's entry requirement), and the
// SERVER reads the same declarations the owner's screen does. A broadcast is the one surface on this
// site where the client and the server disagreeing costs a thousand-person send.
//
// ---------------------------------------------------------------------------
// WHAT THE MEASUREMENT FOUND BEFORE A LINE WAS WRITTEN (R25/R180), AND IT MOVED THE DESIGN TWICE.
//
// **(1) `kind: "house"` HAS EXISTED IN THE NOTIFICATION ROSTER SINCE 063 AND HAS NEVER HAD A
// WRITER — AND IT PUSHES TO NOBODY.** `houseNotificationText` composes it, `houseNotificationTarget`
// lands it, `houseNotificationValid("house")` is TRUE, and `PUSH_DEFAULT_KINDS` did not name it. So
// a broadcast built on it the obvious way writes 1,351 bell rows, calls `pushToMember` **zero**
// times, and reports a clean send. `shouldPush` is the one law that decides, it reads an ABSENT
// preference as the narrow default (R18), and `house` was outside the default — which is correct for
// a kind nobody sends and wrong the moment she sends one. It is on the default roster now, with its
// reason, and a member who has turned that kind off is still honoured because that is the same law.
//
// **(2) A PUSH BROADCAST TO "ALL" REACHES 279 PEOPLE, NOT 1,351.** Measured: 1,351 human members,
// **279 of them carry a push subscription row at all**. The audience count and the REACH of a
// channel are different facts (R18), and showing her one number for both is how a send that touched
// a fifth of the house reads as a send to the house. `channelReach` is a separate figure and the
// preview prints both.
// ---------------------------------------------------------------------------

import { PUSH_TITLE_MAX, brandEchoViolations, pushShapeViolations } from "./notifications.js";
import { houseEmailHtml, houseEmailText } from "../server/email-shell.js";
import { SITE_URL } from "./siteMeta.js";
import { emailShapeValid } from "./emailAddress.js";

// ---------------------------------------------------------------------------
// THE AUDIENCES — HER FOUR CLASSES, DECLARED ONCE, READ BY THE SCREEN AND BY THE SQL.
//
// `key` is what travels on the wire and is written into the ledger; `label` is what she reads. The
// STORE derives its predicate per key from this roster, so an audience the screen can offer that the
// store cannot resolve is impossible rather than a validation gap (R28/R29 — and on this surface the
// cost of two answers is a send to the wrong list of people).
// ---------------------------------------------------------------------------
export const BROADCAST_AUDIENCES = [
  { key: "all", label: "Everyone", note: "Every member of the house." },
  // **HER "PAID (any tier, live subscription or grant)" IS A UNION AND NOT A SUM.** 190 pay and 274
  // hold a live grant, and 53 people are in both — so `paid` is a DISTINCT count over the union and
  // never `paying + granted`, which would tell her she was writing to 464 people and write to 411.
  { key: "paid", label: "Paid members", note: "Anyone with a live subscription or a live grant." },
  // **HER CONVERSION AUDIENCE.** *Never paid* is read strictly: a `subscriptions` row is only ever
  // written by the signed Stripe webhook, so a row of ANY status means money once moved. A member
  // who paid and lapsed has paid, and putting her in the conversion list is writing to a former
  // customer as though she were a stranger.
  { key: "never-paid", label: "Signed up, never paid", note: "No payment ever, and no live grant." },
  { key: "tier-newbie", label: "Newbie", note: "Live subscription at Newbie." },
  { key: "tier-apprentice", label: "Apprentice", note: "Live subscription at Apprentice." },
  { key: "tier-vet", label: "Vet", note: "Live subscription at Vet." },
  // **"ELITE GRANTS" IS A GRANT SOURCE, NOT A TIER, AND THAT IS MEASURED RATHER THAN INFERRED.**
  // There is no `elite` tier anywhere in `server/tiers.js` — `TIER_ORDER` is free/newbie/apprentice/
  // vet. The Elite 2026 cohort holds `access_grants.source = 'elite-2026'` at the APPRENTICE tier
  // (266 of them), so a slice keyed on a tier name would have been empty forever with nothing red.
  { key: "grant-elite", label: "Elite grants", note: "The Elite 2026 cohort, on their complimentary grant." },
  // ==============================================================================================
  // **F4's FUNNEL LEADS — THE ONE AUDIENCE ON THIS ROSTER THAT IS NOT MADE OF MEMBERS.**
  //
  // Her clause: *"Leads who are not members join a FUNNEL LEADS audience in the Broadcast tool,
  // emailable for follow-up per the ceiling rules."* Every other key above resolves `FROM users u`;
  // this one resolves `FROM funnel_leads` — people who gave this house an address off an
  // advertisement and have never signed up. That is why it is not a one-line addition, and it is
  // why `channels` below exists.
  // ==============================================================================================
  {
    key: "funnel-leads",
    label: "Funnel leads",
    note: "Gave their details to a funnel, never signed up.",
    // **EMAIL ONLY, AND IT IS A PROPERTY RATHER THAN A WARNING.** A push subscription is keyed on a
    // member's row; a lead has none, so a push send to this audience reaches ZERO people — and it
    // would do it while the screen showed her a healthy-looking count. Declaring the channel is what
    // lets the composer refuse the tick rather than accept it and mail nobody (R18: *nobody has a
    // device* and *this audience cannot have devices* are different facts).
    channels: ["email"],
  },
  // ==============================================================================================
  // >>> **HER DRESS REHEARSAL — THE ONE AUDIENCE THAT IS *ONLY* HER OWN SEAT.** (2026-08-14) <<<
  //
  // Her order before the never-paid send: *"compose an actual broadcast through the ACTUAL dashboard
  // composer path (not a harness), audience: owner-only test, and SEND it."* **There was no such
  // door**, and there could not be: every audience above resolves through `ownerClause`, which
  // EXCLUDES her seat, and `broadcastRecipientRefusal` refuses it again per person. Both of those are
  // correct and neither was weakened — so the rehearsal is a KEY on this roster rather than a hole in
  // the recipient law.
  //
  // **IT IS THE REAL COMPOSER, THE REAL PREVIEW, THE REAL TYPED CONFIRMATION, THE REAL LEDGER ROW AND
  // THE REAL DETACHED `runBroadcast`** — the only thing that differs is which rows the audience
  // resolves to, which is the one thing a rehearsal has to change. A side door that composed its own
  // mail would prove the side door (R28/R29), which is exactly the *"it worked for us"* she refuses.
  //
  // **IT DECLARES NO `channels`, WHICH IS LOAD-BEARING:** `AUDIENCE_LEAD_KEYS` is DERIVED from an
  // entry that names channels without `push`, so an email-only spelling here would silently make the
  // rehearsal resolve `FROM funnel_leads` and mail strangers instead of her (R11's derivation biting
  // the round that adds to it). Both channels, because the pair is what she is rehearsing.
  // ==============================================================================================
  {
    key: "owner-test",
    label: "Owner test (just me)",
    note: "Your own seat only. A dress rehearsal of the real send, to your inbox.",
  },
];
export const AUDIENCE_KEYS = BROADCAST_AUDIENCES.map((a) => a.key);
export const audienceValid = (key) => AUDIENCE_KEYS.includes(String(key || ""));
export const audienceSpec = (key) => BROADCAST_AUDIENCES.find((a) => a.key === String(key || "")) || null;
export const audienceLabel = (key) => audienceSpec(key)?.label || "";

/**
 * **WHICH CHANNELS AN AUDIENCE CAN ACTUALLY BE REACHED ON.** Absent means both — every member
 * audience — so this is a NARROWING that an entry opts into and never something each one restates
 * (R28/R29: seven entries repeating "push, email" is seven places to forget one).
 */
export const audienceChannels = (key) => audienceSpec(key)?.channels || BROADCAST_CHANNELS;
export const audienceAllowsChannel = (key, channel) =>
  audienceValid(key) && audienceChannels(key).includes(String(channel || ""));

/**
 * **AN AUDIENCE MADE OF LEADS RATHER THAN MEMBERS.** Asked as a PROPERTY of the roster rather than by
 * spelling the key at each call site, because four surfaces need to know (the count, the recipient
 * read, the unsubscribe token's subject and the send loop's notify path) and a fifth will be added
 * by somebody who has not read this comment.
 *
 * **A LEAD AUDIENCE IS EXACTLY AN AUDIENCE THAT CANNOT BE PUSHED**, so the two are held as one
 * biconditional by the fixture rather than as two rosters that could disagree — the disagreement
 * being a push send resolved against a table with no devices in it.
 */
export const AUDIENCE_LEAD_KEYS = BROADCAST_AUDIENCES.filter((a) => a.channels && !a.channels.includes("push")).map((a) => a.key);
export const audienceIsLeads = (key) => AUDIENCE_LEAD_KEYS.includes(String(key || ""));

/**
 * **THE REHEARSAL AUDIENCE, ASKED BY NAME AND NEVER DERIVED FROM A PROPERTY.** `audienceIsLeads`
 * above is derived because *cannot be pushed* really is what makes a lead audience a lead audience.
 * This one is the opposite: nothing about its SHAPE distinguishes it from a member audience, and a
 * property somebody invented to detect it (an `ownerOnly: true` flag, say) would be a second answer
 * to *which key is the rehearsal* — on the one key that is allowed past the owner wall (R28/R29).
 * One literal, exported, read by the store and by the send loop.
 */
export const OWNER_TEST_AUDIENCE = "owner-test";
export const audienceIsOwnerTest = (key) => String(key || "") === OWNER_TEST_AUDIENCE;

/**
 * **THE PILE HER ROSTER DELIBERATELY DOES NOT SLICE, NAMED RATHER THAN LEFT SILENT (R17/R160).**
 *
 * A member who once paid, whose subscription is no longer entitling, and who holds no grant is in
 * `all` and in no other audience: she is not `paid` (nothing is live) and not `never-paid` (money
 * moved). Three people are in that state today. Her order names four classes and this is not one of
 * them, so no slice was invented for it — but a reader counting the slices and finding they do not
 * add to the membership deserves the reason, and a future round asking for a win-back audience has
 * the name already.
 */
export const LAPSED_NOT_SLICED = "A member who paid once, holds nothing live now, and holds no grant is in Everyone and in no slice. Her order names four classes and win-back is not one of them.";

// ---------------------------------------------------------------------------
// WHO NEVER RECEIVES — HER CLAUSE, AND IT IS ASKED IN THE SQL *AND* AGAIN AT THE SEND.
//
// **TWO WALLS BECAUSE THE FAILURE IS UNRECOVERABLE.** A mis-resolved audience that leaks a broadcast
// onto a harness seat is noise; one that puts a marketing send in front of her own owner seat is
// noise she can see. Neither is the reason for the second wall. The reason is that an audience is
// resolved ONCE and a send runs for minutes afterwards in batches, and this house has already paid
// for a trace that notified 118 real members (ITEM 12). The recipient law is asked per recipient.
// ---------------------------------------------------------------------------

/** R144's address convention, matched at the END — `invalid@gmail.com` is a real member. */
export const unreachableAddress = (email) => /\.invalid$/i.test(String(email || "").trim());

/**
 * **MAY THIS SEAT RECEIVE A BROADCAST.** Three refusals, each naming itself, because *"she is a
 * fixture"*, *"she is the house"* and *"there is nowhere to send it"* are three different facts and
 * only the third is about the member (R18).
 *
 * `harness === true` and not a truthy read: this flag arrives off JSON on the route side and
 * `Boolean("false")` is `true` (R136/R137).
 *
 * **`allowOwner` IS ONE CLAUSE WIDE AND DEFAULTS TO FALSE, SO NOTHING CHANGES FOR ANY EXISTING
 * CALLER** (ITEM 7(a)'s `justInstalled` and ITEM 12's ring shipped on exactly this neutrality
 * argument). It is passed TRUE by exactly one thing — the rehearsal audience — and it relaxes ONLY
 * the owner clause: a harness seat and an unroutable address are refused above it and are refused
 * still, because the failure the other two guard against is a fixture or a dead address receiving
 * real marketing mail, and a rehearsal does not make either of those safe. **An owner seat that is
 * also flagged harness stays refused**, which is the order of these clauses doing its job.
 */
export function broadcastRecipientRefusal(seat = {}, { ownerEmails = [], allowOwner = false } = {}) {
  const email = String(seat.email || "").trim().toLowerCase();
  if (!email) return "no-address";
  if (seat.harness === true || seat.isHarness === true) return "harness-seat";
  if (unreachableAddress(email)) return "unreachable-address";
  const owners = ownerEmails instanceof Set ? ownerEmails : new Set((ownerEmails || []).map((e) => String(e || "").trim().toLowerCase()));
  if (owners.has(email) && allowOwner !== true) return "owner-seat";
  return "";
}
export const mayReceiveBroadcast = (seat, opts) => broadcastRecipientRefusal(seat, opts) === "";

// ---------------------------------------------------------------------------
// THE CHANNELS.
// ---------------------------------------------------------------------------
// >>> C9 PUT `sms` ON THIS ROSTER, AND P4's BICONDITIONAL IS WHAT MADE THAT LEGAL. <<<
//
// P4 shipped the reachable COUNT and deliberately left the channel off, with its reason written
// down: *a channel she can tick is a channel the loop really sends on*, held as three facts — the
// send loop BRANCHES on every rostered channel, each branch RESOLVES ITS OWN RECIPIENTS, and the
// store NARROWS per channel. **All three are true of `sms` now** (`runBroadcast`'s third branch,
// `audienceRecipients`' `SMS_REACHABLE_SQL` narrowing, and `server/twilio-send.js` under it), so it
// joins by SATISFYING that law rather than by somebody deciding it was time.
//
// **AND THE PENDING NOTE STOPS DRAWING BY ITSELF** — `smsSendingLive` is DERIVED from this roster
// (see below), so nothing has to be remembered on the day the sender lands.
export const BROADCAST_CHANNELS = ["push", "email", "sms"];
export const channelValid = (c) => BROADCAST_CHANNELS.includes(String(c || ""));
/**
 * Her checkboxes, normalised: unique, in the declared order, and nothing unrostered survives. The
 * filter runs over the ROSTER rather than over what arrived, so the answer is always a subset of
 * `BROADCAST_CHANNELS` no matter what a caller sends — and `channelValid` is asked so there is one
 * answer to what a channel is rather than a second `includes` written here.
 */
export function normalizeChannels(list) {
  const asked = new Set((Array.isArray(list) ? list : []).map((c) => String(c || "")).filter(channelValid));
  return BROADCAST_CHANNELS.filter((c) => asked.has(c));
}

/**
 * >>> **P4 (2026-08-15) — THE REACHABLE-BY-SMS COUNT IS A REPORTED NUMBER, AND `sms` IS NOT A
 * CHANNEL UNTIL SOMETHING CAN SEND ONE.** <<<
 *
 * Her own text separates the two halves — *the COUNT half rides P1-P3*, the SEND half is C9 and C9 is
 * blocked on three Twilio credentials that are absent on this clone. **Putting the tickbox on the
 * screen now is R38/R165's trap on the one path where it costs a send:** she ticks SMS, types the
 * confirmation, and the loop either throws or writes a ledger row for a broadcast that reached
 * nobody, which is a receipt for something that did not happen (R141).
 *
 * `smsSendingLive` is DERIVED from the roster rather than declared beside it, so the day C9 adds a
 * sender and puts `sms` on `BROADCAST_CHANNELS` this note **stops drawing by itself** rather than
 * waiting for somebody to remember it (R31) — and `scripts/broadcast-tool.mjs` holds the absence as a
 * PROPERTY rather than as a literal, so the clause does not go red on the day she legitimately
 * gains the channel.
 */
/**
 * >>> HER BROADCAST REPORT (2026-08-18), DEFECT 2: **THE CHANNEL ROW SHOWED TWO EMAIL CHECKBOXES.**
 * <<<
 *
 * The composer rendered `{c === "push" ? "Push" : "Email"}` — **a two-way ternary over a roster C9
 * made three** — so `sms` came out labelled *Email* and she was looking at the same word twice. Her
 * own read of it was exactly right.
 *
 * **THE LABEL IS A DECLARATION PER CHANNEL RATHER THAN A TERNARY AT THE RENDER**, held as a
 * biconditional against `BROADCAST_CHANNELS`, so a FOURTH channel joins by going RED rather than by
 * silently inheriting somebody else's word. **An unrostered channel has NO label at all** — never a
 * plausible wrong one, which is R25's `DRAWER_ICONS` ruling: a visibly missing name is reportable and
 * a confidently wrong one is what she just spent a morning on.
 *
 * Her words for the third are *TEXT MESSAGE*, and they are hers rather than the machine key.
 */
export const BROADCAST_CHANNEL_LABELS = Object.freeze({
  push: "Push",
  email: "Email",
  sms: "Text message",
});

export function channelLabel(channel) {
  const key = String(channel || "");
  return channelValid(key) && Object.prototype.hasOwnProperty.call(BROADCAST_CHANNEL_LABELS, key)
    ? BROADCAST_CHANNEL_LABELS[key]
    : "";
}

/**
 * >>> HER CLAUSE: **EACH CHANNEL GATES ON ITS RAIL'S ARMED STATE** — *SMS appears only when
 * TWILIO_DAILY_CEILING and the keys arm it, per its law.* <<<
 *
 * **THREE ANSWERS, NEVER TWO (R18), AND THE THIRD IS THE ONE A NAIVE BUILD LOSES.** `unknown` is a
 * payload that has not arrived — the beat before the composer's own read lands — and it is NOT an
 * unarmed rail. Collapsed, every channel flickers off on the first paint of her own screen; read the
 * other way, a channel is offered before anything can send on it, which is the state her report is
 * about.
 *
 * **AND THE ARMED FACT IS THE SERVER'S, BECAUSE ONLY THE SERVER CAN KNOW IT.** A browser cannot see
 * `TWILIO_DAILY_CEILING`, so this takes the rails payload as a PARAMETER and reads it; anything else
 * would be the client guessing at an environment it has never been shown.
 *
 * `push` needs no rail: it is this house's own web-push and is armed by existing.
 */
export const CHANNEL_OFFER = Object.freeze({ OFFER: "offer", HOLD: "hold", UNKNOWN: "unknown" });

export function channelOffer(channel, rails) {
  if (!channelValid(channel)) return CHANNEL_OFFER.HOLD;
  if (channel === "push") return CHANNEL_OFFER.OFFER;
  if (!rails || typeof rails !== "object") return CHANNEL_OFFER.UNKNOWN;
  const rail = rails[String(channel)];
  if (!rail || typeof rail !== "object") return CHANNEL_OFFER.UNKNOWN;
  // Presence, never truthiness: this arrives off JSON and `Boolean("false")` is `true` (R53/R136/
  // R137). Only a real `true` arms a rail that costs money per message.
  return rail.armed === true ? CHANNEL_OFFER.OFFER : CHANNEL_OFFER.HOLD;
}

/**
 * The channels her composer may really offer, given what the server said about the rails.
 *
 * **A CHANNEL SHE CANNOT SEND ON IS NOT RENDERED**, which is her clause read literally — rather than
 * drawn and quietly refused at the door, which is A2's dead box on the one control that costs money.
 */
export function offeredChannels(rails) {
  return BROADCAST_CHANNELS.filter((c) => channelOffer(c, rails) === CHANNEL_OFFER.OFFER);
}

export const SMS_CHANNEL = "sms";

// >>> MIGRATED AT ITS DECLARATION (DL-2/DT-2b/R181): THIS READ THE ROSTER, AND THE ROSTER IS NOT THE
// RAIL. <<< P4's reasoning above is untouched and was exactly right on the day it was written — the
// note had to stop drawing by itself when a sender landed. **What it could not know is that C9 would
// put `sms` on the roster while the Twilio rail stayed unarmed**, and `channelValid` answered TRUE
// the moment the key existed: the note stopped drawing, the tickbox appeared, and nothing could send
// on it. That is the state her 2026-08-18 report is about.
//
// **THE RAIL IS THE FACT AND IT IS THE SERVER'S**, so it arrives as a PARAMETER — the browser cannot
// see TWILIO_DAILY_CEILING. A payload that has not arrived is NOT an armed rail and is not an unarmed
// one either (R18): `channelOffer` keeps the three apart and this asks it, so there is one answer to
// *may she send a text* rather than a second `includes` written here (R28/R29).
export const smsSendingLive = (rails) => channelOffer(SMS_CHANNEL, rails) === CHANNEL_OFFER.OFFER;

// >>> C9 — **WHAT A BROADCAST SAYS IN A TEXT.** <<<
//
// **IT IS NOT THE EMAIL BODY AND IT IS NOT THE PUSH LINE (R18).** The email carries her whole block
// stack and a footer; a push carries a title and a taste. A text is one plain message with no markup
// of any kind, and it costs money per segment — so it is composed rather than borrowed.
//
// **THE SUBJECT LEADS, BECAUSE ON A LOCK SCREEN THAT IS ALL SHE MAY SEE.** The body follows only
// when it says something the subject did not (P5's own reasoning on a push title: a second line
// repeating the first spends her attention on nothing).
//
// **AND THE STOP NOTICE IS NOT OPTIONAL AND IS NOT A COMMENT.** Carriers require a marketing message
// to say how to stop. It rides the composed body, so a caller cannot forget it — which is exactly
// what `broadcastEmailHtml` does with the unsubscribe footer one channel over, for the same reason.
export const SMS_STOP_NOTICE = "Reply STOP to opt out.";

// **A TEXT CARRIES NO MARKUP, AND THAT IS STRIPPED HERE RATHER THAN TRUSTED.** Her body is composed
// for an email, where `<b>` is emphasis; in a text it is four wasted characters a member reads as
// broken. The tags go and their CONTENT stays, so a bolded word survives as a word.
//
// **THE LIMIT IS NAMED RATHER THAN HIDDEN (R17/R160):** the handful of entities a composer really
// produces are decoded and no general HTML parser is written for a 160-character message. An entity
// this does not know reaches her as itself, which is visible and reportable — the direction that
// costs a character rather than a meaning.
const SMS_ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'", "&nbsp;": " " };
function plainForSms(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&amp;|&lt;|&gt;|&quot;|&#39;|&nbsp;/g, (m) => SMS_ENTITIES[m])
    .replace(/\s+/g, " ")
    .trim();
}

export function broadcastSmsText({ subject = "", emailBody = "", body = "" } = {}) {
  const line = plainForSms(subject);
  const rest = plainForSms(body || emailBody);
  // A message with nothing to say is EMPTY rather than a lone STOP notice — a text that is only an
  // opt-out instruction is a send she paid for that told nobody anything (T9-2's no-body-no-box law,
  // on a metered rail).
  if (!line && !rest) return "";
  const lead = line || rest;
  const tail = rest && rest !== line ? ` ${rest}` : "";
  return `${lead}${tail} ${SMS_STOP_NOTICE}`.trim();
}
export const SMS_PENDING_NOTE = "Text sending is not switched on yet, so this is who it would reach.";
export const smsSendingNote = (rails) => (smsSendingLive(rails) ? "" : SMS_PENDING_NOTE);

/**
 * **THE SENTENCE UNDER HER PICKER, AS A LAW A GATE RUNS RATHER THAN A REGEX OVER JSX** (ITEM 8's own
 * repair one dashboard strip over: a fixture quoting an inline shape certifies the spelling, not the
 * behaviour).
 *
 * **A REACH THAT IS NOT A REAL NUMBER IS OMITTED RATHER THAN DRAWN AS ZERO (R18/R65).** A tab left
 * open across the deploy that adds a reach holds a payload without it, and `0 by text` on this screen
 * reads as *nobody has given a number*, which is a fact about her members rather than about a stale
 * page. Absent is not zero — and `Number(true)` is 1, so presence is asked as a real finite number
 * rather than as truthiness (R53/R136/R137).
 */
export function audienceReachLine(counts) {
  if (!counts || typeof counts !== "object") return "";
  const n = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const people = n(counts.people);
  if (people === null) return "";
  const parts = [];
  if (n(counts.pushReach) !== null) parts.push(`${counts.pushReach} can be reached by push`);
  if (n(counts.emailReach) !== null) parts.push(`${counts.emailReach} by email`);
  if (n(counts.smsReach) !== null) parts.push(`${counts.smsReach} by text`);
  return parts.length ? `${people} people. ${parts.join(", ")}.` : `${people} people.`;
}

// ---------------------------------------------------------------------------
// THE COMPOSITION LAW. What may be sent, and why each clause is here.
// ---------------------------------------------------------------------------
export const BROADCAST_BODY_MAX = 2000;
export const BROADCAST_SUBJECT_MAX = 140;
/**
 * The grey line a mail client shows beside the subject in the list. Clients cut it around here, and
 * a preheader longer than the cut is words she wrote that nobody will ever read.
 */
export const EMAIL_PREHEADER_MAX = 160;

// ---------------------------------------------------------------------------
// >>> **THE BLOCK COMPOSER — HER MARKETING TOOL (owner, 2026-08-13; built 2026-08-14).** <<<
//
// Her order: the email body stops being one text box and becomes a stack of BLOCKS she orders
// herself — TEXT, HEADING, BUTTON, IMAGE, DIVIDER — **owner-friendly, no code, no design skill
// needed**, with a live preview beside it and everything rendering through the E3 shell.
//
// **THIS FILE HOLDS THE MODEL AND NEVER THE MARKUP, AND THAT SPLIT IS E3's LAW RATHER THAN A
// PREFERENCE.** The house's email look exists in exactly one file (`server/email-shell.js`) and a
// second place painting an email is a second house — so what lives here is WHICH KINDS EXIST, WHAT
// EACH MUST CARRY, and WHICH URLS ARE LEGAL IN A MAIL. The shell draws them.
//
// **AND THE STRING BODY IS NOT RETIRED, WHICH IS THE HALF THAT PROTECTS WHAT IS ALREADY IN THE
// LEDGER.** Every broadcast sent before today is a row with `email_body` and no blocks, and the
// history screen and any re-read must still render it. A plain string resolves to a single stack of
// text blocks, so there is ONE path into the shell and the old rows come out byte-identical
// (FREE-1's `action` default: omitting the new argument is exactly today's behaviour).
// ---------------------------------------------------------------------------

/**
 * **HER FIVE, DECLARED ONCE AND READ BY THE SCREEN, THE VALIDATOR, THE STORE AND THE RENDERER.**
 * `fields` is what the composer draws for each kind; `words` says whether this kind can carry any of
 * the mail's sentences, which is what the no-words refusal below is asked of.
 */
export const EMAIL_BLOCK_KINDS = [
  { kind: "text", label: "Text", fields: ["text"], words: true },
  { kind: "heading", label: "Heading", fields: ["text"], words: true },
  { kind: "button", label: "Button", fields: ["label", "href"], words: true },
  { kind: "image", label: "Image", fields: ["src", "alt"], words: false },
  { kind: "divider", label: "Divider", fields: [], words: false },
];
export const EMAIL_BLOCK_KEYS = EMAIL_BLOCK_KINDS.map((b) => b.kind);
export const emailBlockSpec = (kind) => EMAIL_BLOCK_KINDS.find((b) => b.kind === String(kind || "")) || null;
export const emailBlockValid = (kind) => Boolean(emailBlockSpec(kind));

/**
 * **A MAIL CLIENT FETCHES EVERY URL FROM THE OPEN INTERNET, SO A RELATIVE ONE IS A BROKEN PICTURE
 * AND A DEAD BUTTON.** `EMAIL_HEADER_IMAGE` already states this law for the header; her IMAGE and
 * BUTTON blocks are the same fact, so the same rule applies to both rather than each remembering.
 *
 * **A HOUSE PATH IS RESOLVED RATHER THAN REFUSED**, because that is what the art picker hands over
 * and refusing it would wall her out of her own files; an absolute `http(s)` URL passes, because a
 * marketing mail legitimately links out. **Everything else is REFUSED and never guessed at** —
 * `javascript:` and `data:` are the two that matter, and escaping does not cover a scheme.
 */
export function emailAbsoluteUrl(value) {
  const raw = String(value == null ? "" : value).trim();
  if (!raw) return "";
  if (raw.startsWith("//")) return "";           // protocol-relative: the scheme is the mail client's guess
  if (raw.startsWith("/")) return `${String(SITE_URL).replace(/\/+$/, "")}${raw}`;
  if (/^https?:\/\/\S+$/i.test(raw)) return raw;
  return "";
}

// ---------------------------------------------------------------------------
// >>> **HER BUTTON RULING (2026-08-14): THE DESTINATION IS FULLY OWNER-DIRECTED, AND MOST OF IT WAS
// ALREADY TRUE BY CONSTRUCTION — WHICH IS THE ITEM (R19).** <<<
//
// *"She can point it at ANY site destination: the home page, a specific room, a specific reading or
// generator, a funnel door, any path on 12thhouse.ai."* `emailAbsoluteUrl` above has resolved any
// house path since the block composer shipped and passes any `http(s)` URL, so **nothing needed
// widening and nothing was widened** — a round that "added" that capability would be a change with
// no defect under it. What was genuinely missing is the PICKER (so she never hand-types), the
// EXTERNAL FLAG, and the LEDGER RECORD, and those are the three things below and in the composer.
//
// **THE LANDING LAW RIDES FREE BY CONSTRUCTION AND IS NOT RE-IMPLEMENTED HERE.** A recipient tapping
// a gated reading lands ON that reading's page and FREE-1's gates do the selling (explore free, the
// purple box at the generation moment); a paid member lands straight in. A button that tried to
// pre-judge entitlement would be a second answer to what a member may open (R20/R28/R29) — composed
// in an email, minutes or days before she taps it, against a tier that may have changed since.
// ---------------------------------------------------------------------------

/** The house's own origin, for the one comparison below. Derived from `SITE_URL`, never re-spelled. */
const siteOrigin = () => String(SITE_URL).replace(/\/+$/, "");

/**
 * **DOES THIS BUTTON LEAVE THE HOUSE.** Her clause: *"External URLs allowed but flagged visually as
 * leaving the site."* ALLOWED is the operative half — this answers a question the composer DRAWS a
 * mark from and never a refusal, so a legitimate outbound link is never blocked by it.
 *
 * A blank or refused destination is **not** external: it is nothing, and marking it *leaving the
 * site* would put a warning on a button she has not finished writing (R18 — two facts).
 */
export function isExternalDestination(href) {
  const url = emailAbsoluteUrl(href);
  if (!url) return false;
  return !url.toLowerCase().startsWith(siteOrigin().toLowerCase());
}

/**
 * **EVERY BUTTON'S DESTINATION, FOR HER LEDGER ROW.** Her clause names it, and the honest reading is
 * that it must be READABLE rather than merely present: the resolved hrefs are already inside the
 * stored `blocks`, so this composes no new truth — it LIFTS the one field an audit asks about out of
 * a nested structure so the ledger line answers *where did that send point people* without anybody
 * re-parsing a stack (R18: stored and legible are two facts).
 *
 * It is derived from the NORMALISED blocks, so a destination in the ledger is the destination that
 * really went out — never the raw one she typed, which may have been refused by `emailAbsoluteUrl`
 * and drawn no button at all.
 */
export function buttonDestinations(blocks) {
  return normalizeBlocks(blocks)
    .filter((b) => b.kind === "button" && String(b.href || "").trim())
    .map((b) => ({ label: String(b.label || ""), href: String(b.href), external: isExternalDestination(b.href) }));
}

/**
 * **THE ONE DOOR EVERY BLOCK COMES THROUGH.** It coerces the shape, trims, and resolves the two URL
 * fields — so nothing downstream has to remember either, and the shell can never be handed a
 * relative `src` because nothing hands it one.
 *
 * **A BLOCK IS NEVER SILENTLY DROPPED HERE.** An unrostered kind survives normalisation carrying its
 * own kind, so `blockViolations` can NAME it; dropping it would make a mail that quietly lost a
 * block pass every check there is (R19: the silent direction).
 */
export function normalizeBlocks(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((b) => b && typeof b === "object").map((b) => {
    const kind = String(b.kind || "");
    if (kind === "text" || kind === "heading") return { kind, text: String(b.text == null ? "" : b.text).trim() };
    if (kind === "button") return { kind, label: String(b.label == null ? "" : b.label).trim(), href: emailAbsoluteUrl(b.href) };
    if (kind === "image") return { kind, src: emailAbsoluteUrl(b.src), alt: String(b.alt == null ? "" : b.alt).trim() };
    if (kind === "divider") return { kind };
    return { kind };
  });
}

/** How many blocks a body may hold. A cap she can see, rather than a mail nobody can scroll. */
export const EMAIL_BLOCKS_MAX = 40;

/** A template's name is a label on a picker, not a body. Long enough to be descriptive, short
 *  enough that the picker stays readable. */
export const TEMPLATE_NAME_MAX = 80;
export const TEMPLATE_SAVED = "Saved. It is in your templates.";
export const TEMPLATE_NEEDS_NAME = "Give the template a name first.";

/**
 * **THE ONE THING HER LIVE PREVIEW CANNOT CARRY, NAMED RATHER THAN FAKED.** An unsubscribe token is
 * signed on the server and is one per member, so the composer beside her stack shows a sample link.
 * The words, the dress and every block are the real mail — this is the only difference, and it is
 * printed under the preview rather than left for her to discover by tapping it.
 */
export const PREVIEW_UNSUBSCRIBE_TOKEN = "preview";
export const PREVIEW_LINK_NOTE = "This is the real email. Only the unsubscribe link is a sample here — every member gets her own.";

/**
 * **EVERY REASON A BLOCK STACK MAY NOT BE SENT, NAMED PER BLOCK SO SHE KNOWS WHICH ONE.**
 *
 * The URL clauses fire when the field was FILLED IN and did not resolve — an empty href is
 * `button-no-link` and a rejected one is `button-link-not-allowed`, because *she left it blank* and
 * *we will not send that* are two different facts and only the second one is about what she typed
 * (R18).
 */
export function blockViolations(list) {
  const blocks = Array.isArray(list) ? list : [];
  const hits = [];
  if (blocks.length > EMAIL_BLOCKS_MAX) hits.push({ label: "email-too-many-blocks" });
  blocks.forEach((b, i) => {
    const at = `:${i + 1}`;
    const kind = String(b?.kind || "");
    if (!emailBlockValid(kind)) { hits.push({ label: `block-unknown-kind${at}` }); return; }
    if ((kind === "text" || kind === "heading") && !String(b.text || "").trim()) hits.push({ label: `block-empty${at}` });
    if (kind === "button") {
      if (!String(b.label || "").trim()) hits.push({ label: `button-no-label${at}` });
      if (!String(b.href || "").trim()) hits.push({ label: `button-no-link${at}` });
    }
    if (kind === "image" && !String(b.src || "").trim()) hits.push({ label: `image-no-picture${at}` });
  });
  return hits;
}

/**
 * The words in a stack, joined the way the shell's text part joins them — so the length she is
 * capped at is the length that really goes out, and a body of pictures and rules is measurably
 * wordless.
 */
export function blocksText(list) {
  return (Array.isArray(list) ? list : []).map((b) => {
    const kind = String(b?.kind || "");
    if (kind === "text" || kind === "heading") return String(b.text || "").trim();
    if (kind === "button") {
      const href = String(b.href || "").trim();
      return href ? `${String(b.label || "Open").trim()}: ${href}` : String(b.label || "").trim();
    }
    return "";
  }).filter(Boolean).join("\n\n");
}

/**
 * >>> **THE ONE RESOLVER: WHAT THIS BROADCAST'S EMAIL BODY IS.** <<<
 *
 * Read by the validator, the preview, the send door and both composers. **A draft can arrive
 * carrying both a block stack and a stale text box** — the screen keeps the old field while she
 * builds — and a validator that reads one while the renderer reads the other is a broadcast that
 * passes every check and sends something else, to everybody, once (R28/R29).
 *
 * **BLOCKS WIN WHEN THERE ARE ANY**, because a stack is what she built most recently and the text
 * box is what the row carried before it.
 */
export function emailContent(draft = {}) {
  const blocks = normalizeBlocks(draft.blocks);
  if (blocks.length) {
    const text = blocksText(blocks);
    return { mode: "blocks", blocks, text, words: Boolean(text.trim()) };
  }
  const body = String(draft.emailBody || "").trim();
  const paragraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  const text = paragraphs.join("\n\n");
  return {
    mode: "text",
    blocks: paragraphs.map((text_) => ({ kind: "text", text: text_ })),
    text,
    words: Boolean(text),
  };
}
/** The push body a lock screen can hold. `pushShapeViolations` holds the title; this holds the body. */
export const BROADCAST_PUSH_BODY_MAX = 300;

/**
 * **EVERY REASON A BROADCAST MAY NOT BE SENT, AS LABELS A SCREEN CAN RENDER AND A GATE CAN DRIVE.**
 *
 * The push half is `pushShapeViolations` + `brandEchoViolations` — **imported, never re-typed**. P5's
 * three-line law says line one is iOS's own app-name header and this house spends no field on it, so
 * a broadcast whose title says "12th House" is the brand echoed into the one field that already
 * carries it. Those two laws already hold every other push on this site; a broadcast is the widest
 * push this house will ever send and is the last place a second spelling of them belongs.
 */
export function broadcastViolations(draft = {}) {
  const hits = [];
  const channels = normalizeChannels(draft.channels);
  if (!audienceValid(draft.audience)) hits.push({ label: "no-audience" });
  if (!channels.length) hits.push({ label: "no-channel" });
  // **F4 — A CHANNEL THIS AUDIENCE CANNOT BE REACHED ON IS A REFUSAL, NOT A ZERO.** The funnel-leads
  // audience is made of people with no member row, so a push send resolves to nobody — and it would
  // do it after she typed `SEND TO N PEOPLE` against a healthy count. This is asked of the ROSTER, so
  // an audience declaring its channels is refused by existing rather than by a key spelled here.
  for (const channel of channels) {
    if (!audienceAllowsChannel(draft.audience, channel)) hits.push({ label: `channel-not-for-audience:${channel}` });
  }

  if (channels.includes("push")) {
    const title = String(draft.title || "").trim();
    const body = String(draft.body || "").trim();
    if (!title) hits.push({ label: "push-title-empty" });
    if (!body) hits.push({ label: "push-body-empty" });
    if (body.length > BROADCAST_PUSH_BODY_MAX) hits.push({ label: "push-body-too-long" });
    // The shared laws. The payload shape they expect is title/body/url.
    // >>> ONE READING OF THE URL, NOT TWO (R28/R29). <<< This defaulted and the check below read the
    // raw field, so an EMPTY url — what a member leaves when she does not want the push to open
    // anything — failed `"".startsWith("/")` and took her Send control away on every audience. An
    // absent url IS the house's own front door; a url that names somewhere else is the defect this
    // law is about. Measured on production as the owner seat, 2026-08-19.
    const url = String(draft.url || "/");
    const payload = { title, body, url };
    hits.push(...pushShapeViolations(payload));
    hits.push(...brandEchoViolations(payload));
    if (!url.startsWith("/")) hits.push({ label: "push-url-not-in-house" });
  }

  if (channels.includes("email")) {
    const subject = String(draft.subject || "").trim();
    if (!subject) hits.push({ label: "email-subject-empty" });
    if (subject.length > BROADCAST_SUBJECT_MAX) hits.push({ label: "email-subject-too-long" });
    if (String(draft.preheader || "").trim().length > EMAIL_PREHEADER_MAX) hits.push({ label: "email-preheader-too-long" });
    // **THE CONTENT IS RESOLVED ONCE AND THE SAME RESOLUTION IS WHAT RENDERS** (see `emailContent`).
    // A validator reading the blocks while the composer reads the text box is how a broadcast passes
    // every check and sends something else.
    const content = emailContent(draft);
    if (!content.words) hits.push({ label: "email-body-empty" });
    if (content.text.length > BROADCAST_BODY_MAX) hits.push({ label: "email-body-too-long" });
    hits.push(...blockViolations(content.blocks));
  }
  return hits;
}
export const broadcastReady = (draft) => broadcastViolations(draft).length === 0;

/**
 * The push a broadcast really sends, in the `house` notification shape — so it travels the SAME
 * `notifyMember` door every site notification travels, gets the SAME leak net (P2-4C) and lands in
 * her bell as well as on her phone. Nothing here composes a payload of its own.
 */
export function broadcastNotification(draft = {}) {
  return {
    kind: "house",
    title: String(draft.title || "").trim(),
    body: String(draft.body || "").trim(),
    url: String(draft.url || "/") || "/",
  };
}

// ---------------------------------------------------------------------------
// THE TYPED CONFIRMATION — HER SAFETY ON THE TRIGGER.
//
// **THE NUMBER IN THE PHRASE IS THE SERVER'S COUNT, NEVER THE CLIENT'S, AND THAT IS THE WHOLE
// SAFETY PROPERTY.** If the phrase were checked against the number the browser had in hand, a screen
// left open while the membership grew would confirm `SEND TO 12 PEOPLE` and send to 1,351 — the
// exact accident her clause exists to prevent, wearing a correct-looking confirmation. So the server
// re-resolves the audience at the moment of the send, composes the phrase from ITS OWN count, and
// compares. A count that moved in between does not send: she is told the number changed and types
// the new one. That refusal is a feature and is named separately (R18).
// ---------------------------------------------------------------------------
/**
 * >>> HER BROADCAST REPORT (2026-08-18), DEFECT 1: **THE SEND PATH WAS GONE.** <<<
 *
 * **MEASURED AGAINST THE REAL PREVIEW DOOR BEFORE A LINE WAS WRITTEN (R25/R180), OVER EVERY AUDIENCE
 * AND EVERY CHANNEL COMBINATION.** A push-only broadcast produces a Send control on every audience
 * but one. What produces NO Send is a real refusal: the day's mail allowance cannot carry this list,
 * the audience cannot be reached on a channel she ticked, or a rail she ticked is not armed.
 *
 * **AND IN EVERY ONE OF THOSE CASES THE SCREEN SAID *"This cannot be sent yet."* AND NOTHING ELSE.**
 * That is A2's dead box on the one control that costs money: a sentence with no reason under it, no
 * way to act, and — read by somebody who has just composed a broadcast — indistinguishable from the
 * send path having disappeared. Which is exactly how she read it.
 *
 * **SO A REFUSAL NAMES ITSELF (R18: several facts, several sentences).** The reasons are asked in the
 * order she can act on them, and each one is her language rather than the machinery (S1-A/R117): she
 * does not have a `capacity.may`, she has a mailbox with a number of messages left in it today.
 *
 * `maySend` is unchanged and is still the only thing that gates the control — this says WHY, and a
 * screen that could show a reason while the control was live would be two answers to one question
 * (R28/R29), so the law answers "" when it may send.
 */
/**
 * >>> FOUND IN HER OWN CAPTURE: **THE PREVIEW WAS PRINTING RAW MACHINE KEYS AT HER.** <<<
 *
 * The list above the preview rendered `preview.violations` straight — so her screen said
 * `channel-not-for-audience:push` and `push-title-empty`. **Every one of those labels is REFUSED by
 * her own plain-language law** (`plainLanguageViolations`, ITEM A: no machine key, no ruling code, no
 * underscore anywhere outside Advanced), and Broadcasts is one of her six sections.
 *
 * It compounds exactly what she reported: told she cannot send, and told it in a language that names
 * the machinery rather than the thing to change. **A sentence per label, declared once**, held as a
 * biconditional against every label `broadcastViolations` can really write — so a new one added to
 * that law goes RED here rather than reaching her screen as a key.
 *
 * **AN UNROSTERED LABEL STILL SAYS SOMETHING SHE CAN ACT ON AND NEVER THE KEY** (R25's `DRAWER_ICONS`
 * ruling): the shared push-shape and brand laws own their own labels and may add one without this
 * file knowing, so the fallback is honest and general rather than a machine word she cannot read.
 */
export const BROADCAST_VIOLATION_COPY = Object.freeze({
  "no-audience": "Pick who this is going to.",
  "no-channel": "Pick how it goes out.",
  "push-title-empty": "The push needs a title.",
  "push-body-empty": "The push needs a line underneath the title.",
  "push-body-too-long": "The push line is too long for a phone to show. Shorten it.",
  "push-url-not-in-house": "The push has to open somewhere in the house. Start it with a slash.",
  "title-too-long-for-one-bold-line": "The title is too long for the one bold line a phone gives it.",
  "brand-echo-in-the-title": "The title says the house's name, which the phone already shows above it.",
  "brand-echo-in-the-body": "The line says the house's name, which the phone already shows above it.",
  "doubled-branding": "The house's name is in there twice.",
  "nothing-to-say": "There is nothing in this to send yet.",
  "email-subject-empty": "The email needs a subject.",
  "email-subject-too-long": "The email subject is too long. Shorten it.",
  "email-preheader-too-long": "The preview line under the subject is too long. Shorten it.",
  "email-body-empty": "The email needs some words in it.",
  "email-body-too-long": "The email is too long to send. Shorten it.",
});

/** What a label she has never been shown says instead of its key. */
export const VIOLATION_UNKNOWN = "Something in the message still needs fixing before it can go out.";

/** The channel-specific one, so her own word for the channel is what she reads. */
export const CHANNEL_NOT_FOR_AUDIENCE_PREFIX = "channel-not-for-audience:";

export function violationSentence(label) {
  const key = String(label || "");
  if (!key) return VIOLATION_UNKNOWN;
  if (key.startsWith(CHANNEL_NOT_FOR_AUDIENCE_PREFIX)) {
    const channel = key.slice(CHANNEL_NOT_FOR_AUDIENCE_PREFIX.length);
    const word = channelLabel(channel);
    // A channel this file cannot name is still a sentence she can act on, never a key.
    return word
      ? `This audience cannot be reached by ${word.toLowerCase()}. Untick it.`
      : "This audience cannot be reached on one of the channels you picked. Untick it.";
  }
  return Object.prototype.hasOwnProperty.call(BROADCAST_VIOLATION_COPY, key)
    ? BROADCAST_VIOLATION_COPY[key]
    : VIOLATION_UNKNOWN;
}

export const SEND_BLOCK = Object.freeze({
  READY: "",
  NO_AUDIENCE: "Pick who this is going to first.",
  NO_CHANNEL: "Pick how it goes out — at least one of Push, Email or Text message.",
  NOBODY: "There is nobody in this audience right now, so there is no one to send to.",
  NOT_COMPOSED: "Something is still missing above — the notes under Preview say what.",
  CHANNEL_NOT_FOR_AUDIENCE: "This audience cannot be reached that way. Untick the channel it cannot use.",
  MAIL_CAPACITY: "Today's email allowance cannot carry a list this size. Send it to a smaller group, or send the email part tomorrow.",
  SMS_NOT_ARMED: "Text sending is not switched on yet, so a broadcast cannot go out by text. Untick Text message.",
});

/**
 * WHY she cannot send, or "" when she can.
 *
 * The order is what she can DO about it, soonest first: a missing audience or channel is one tap; a
 * channel this audience cannot take is one tap; an unarmed rail is one tap; the mail allowance is a
 * decision about the send itself. **The composition faults come last** because the preview already
 * lists them one by one directly above this sentence, so leading with them would say twice what is
 * already on the screen.
 */
export function sendBlockReason(input = {}) {
  const {
    audience = "", channels = [], people = 0,
    violations = [], mailMay = true, smsArmed = false,
  } = input;
  const chans = normalizeChannels(channels);
  if (!audienceValid(audience)) return SEND_BLOCK.NO_AUDIENCE;
  if (!chans.length) return SEND_BLOCK.NO_CHANNEL;

  const labels = (Array.isArray(violations) ? violations : []).map((v) => String(v && v.label ? v.label : v));
  if (labels.some((l) => l.startsWith("channel-not-for-audience:"))) return SEND_BLOCK.CHANNEL_NOT_FOR_AUDIENCE;
  if (chans.includes(SMS_CHANNEL) && smsArmed !== true) return SEND_BLOCK.SMS_NOT_ARMED;
  // Presence, never truthiness: this count arrives off JSON and `Number(true)` is 1 (R53/R136/R137).
  const n = typeof people === "number" && Number.isFinite(people) ? people : 0;
  if (n <= 0) return SEND_BLOCK.NOBODY;
  if (chans.includes("email") && mailMay !== true) return SEND_BLOCK.MAIL_CAPACITY;
  if (labels.length) return SEND_BLOCK.NOT_COMPOSED;
  return SEND_BLOCK.READY;
}

export const confirmPhrase = (n) => `SEND TO ${Number(n) || 0} PEOPLE`;

/**
 * Four outcomes, not two (R18). `empty` is nothing typed; `count-moved` is a phrase that is a valid
 * confirmation of a DIFFERENT number — which is the one case that must never be read as a typo,
 * because it is the case where the audience really changed under her; `mismatch` is anything else;
 * `ok` sends.
 *
 * The comparison trims and collapses internal whitespace and is otherwise EXACT, case included. A
 * case-insensitive read is the laxer direction and this is the last control before a thousand
 * messages leave the house.
 */
export function confirmationVerdict({ typed = "", count = 0 } = {}) {
  const said = String(typed || "").trim().replace(/\s+/g, " ");
  if (!said) return { ok: false, reason: "empty" };
  if (said === confirmPhrase(count)) return { ok: true, reason: "ok" };
  const m = /^SEND TO (\d+) PEOPLE$/.exec(said);
  if (m) return { ok: false, reason: "count-moved", typedCount: Number(m[1]) };
  return { ok: false, reason: "mismatch" };
}

export const CONFIRM_SENTENCES = {
  empty: "Type the confirmation exactly as it is shown to send.",
  "count-moved": "The audience changed while this was open. Check the new number and type it again.",
  mismatch: "That is not the confirmation. Type it exactly as it is shown.",
};
export const confirmSentence = (reason) => CONFIRM_SENTENCES[String(reason || "")] || CONFIRM_SENTENCES.mismatch;

// ---------------------------------------------------------------------------
// THE UNSUBSCRIBE — THE EMAIL LAW'S REQUIREMENT, AND THE TWO THINGS IT IS NOT.
//
// **IT IS NOT THE PUSH UNSUBSCRIBE.** `/api/push/unsubscribe` sweeps a browser's push subscription;
// this is a member telling the house not to send her MAIL. Two different facts on two different
// carriers (R18), and collapsing them would mean turning off a phone's buzzing silently stopped her
// email too, or worse, the other way round.
//
// **IT IS NOT TRANSACTIONAL MAIL.** Her clause says transactional mail is unaffected. Measured:
// **this repo sends no mail of any kind today** — `GMAIL_*` credentials exist in the environment and
// nothing has ever read them — so there is no transactional stream for this to affect. The flag is
// therefore declared as scoped to BROADCASTS from the day it is minted, rather than as a global
// "email off" somebody widens later without noticing what it now silences.
// ---------------------------------------------------------------------------
export const UNSUBSCRIBE_SCOPE = "broadcast";
export const UNSUBSCRIBE_PATH = "/unsubscribe";
export const UNSUBSCRIBE_SENTENCE = "You will not get any more announcement emails from the house.";
export const UNSUBSCRIBE_LINK_TEXT = "Unsubscribe from announcements";

// Her wardrobe order: every broadcast carries ONE GOLD BUTTON HOME. Declared once, so a hundred
// broadcasts cannot each invent their own way back into the house.
export const BROADCAST_HOME_CTA = "COME INSIDE";

/**
 * >>> THE HOUSE'S GOLD BUTTON IS A FALLBACK, NOT AN ADDITION — AND IT WAS AN ADDITION. <<<
 *
 * **FOUND BY HER OWN COSTUME VERIFICATION, 2026-08-21, ON THE RENDERED MARKUP.** The wardrobe order
 * (2026-08-13) gave every broadcast a gold button home for a stated and correct reason: *"the costume
 * every broadcast wears had no call to action at all — a marketing mail that tells a member the rooms
 * are open and gives her nothing to tap."* True on the day it shipped. **Her BLOCK COMPOSER shipped
 * afterwards and lets her put her own button in** — so a broadcast carrying one rendered TWO GOLD
 * PILLS STACKED, hers and the house's, **both going to the same place**, with nothing red anywhere.
 * R68's vanished-anchor shape one layer up: a law written when the premise was true, left standing
 * after the premise moved.
 *
 * So the question is two facts and not one (R18): *does this mail give her somewhere to go* and *did
 * the house have to supply it*. If her stack already carries a button, the house's own is not drawn —
 * **one button, hers, because she chose it and it is the one she wrote a label for.** If it does not,
 * the wardrobe order stands exactly as it was and nothing changes for the broadcasts she has already
 * sent (ITEM 12's neutrality argument).
 *
 * **IT IS A PROPERTY OF THE RESOLVED STACK RATHER THAN OF THE DRAFT** — asked of what will really be
 * DRAWN, so a button block the normaliser dropped cannot silently take her way home with it.
 */
export function broadcastHomeCta(blocks) {
  const list = Array.isArray(blocks) ? blocks : [];
  const hers = list.some((b) => String(b?.kind || "") === "button" && String(b?.href || "").trim());
  return hers ? null : { label: BROADCAST_HOME_CTA, href: SITE_URL };
}
export const UNSUBSCRIBE_ALREADY = "You were already unsubscribed from announcements.";
export const UNSUBSCRIBE_BAD_LINK = "That link is not one of ours, or it has been changed. Write to us and we will take you off by hand.";

export const unsubscribeUrl = (origin, token) =>
  `${String(origin || "").replace(/\/+$/, "")}${UNSUBSCRIBE_PATH}?t=${encodeURIComponent(String(token || ""))}`;

/**
 * **THE FOOTER IS COMPOSED HERE AND NOWHERE ELSE, SO AN EMAIL WITHOUT ONE CANNOT BE BUILT.** The
 * body-building function below takes the token and refuses to answer without it — that is what makes
 * *"every broadcast email carries a working unsubscribe"* a property of the code rather than a
 * clause somebody has to remember on the day she writes the copy.
 */
export function unsubscribeFooterHtml(url) {
  const href = String(url || "");
  if (!href) return "";
  return `<p style="margin:24px 0 0;font-size:12px;line-height:18px;color:#8a8175">`
    + `<a href="${escapeHtml(href)}" style="color:#8a8175">${escapeHtml(UNSUBSCRIBE_LINK_TEXT)}</a>`
    + `</p>`;
}
export function unsubscribeFooterText(url) {
  const href = String(url || "");
  return href ? `\n\n${UNSUBSCRIBE_LINK_TEXT}: ${href}\n` : "";
}

/** Minimal, total, and applied to every interpolation below. Her copy is typed by a person. */
export function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * **THE HOUSE LOOK, IN THE PALETTE AND NEVER AN INVENTED COLOUR** (THE COLOUR LAW): black ground,
 * gold heading, cream text. Written as inline styles because that is the only styling a mail client
 * reliably keeps, and with a plain-text alternative beside it because a mail with no text part is
 * the shape a spam filter reads worst.
 *
 * **IT REFUSES TO BUILD WITHOUT AN UNSUBSCRIBE URL.** Returning `null` rather than a body without a
 * footer is what makes the email law structural: there is no path through this function that
 * produces a sendable body with no way out of the list.
 */
export function broadcastEmailHtml({ subject = "", body = "", blocks = null, preheader = "", unsubscribe = "" } = {}) {
  const href = String(unsubscribe || "");
  if (!href) return null;
  // **THE SAME RESOLUTION THE VALIDATOR READ.** Never a second split of the body here, or the mail
  // that was checked and the mail that goes out are two different mails (R28/R29).
  const content = emailContent({ emailBody: body, blocks });
  if (!content.blocks.length) return null;
  // E3 (owner, 2026-08-13): the third of three composers that each hand-rolled the house look, each
  // with its own copies of the black, the cream and the gold and none of them carrying her header
  // image. It renders through the ONE shell now. **The unsubscribe footer stays this composer's
  // own** and is passed IN rather than absorbed, because it is a legal requirement of a BROADCAST
  // and not of every mail — folding it into the shell would put an unsubscribe link on her welcome
  // email and her grace reminders, which is the transactional-mail clause broken by a tidy-up.
  return houseEmailHtml({
    heading: subject,
    // Her preheader field. Left out, the client makes one up out of the first thing it finds — which
    // on an image-led mail is the ALT text of her banner, so every announcement in her members'
    // inboxes previews as the site name (the shell's own note, one caller on).
    preheader: String(preheader || "").trim(),
    blocks: content.blocks,
    // >>> **HER WARDROBE ORDER, 2026-08-13: ONE GOLD BUTTON HOME.** <<< The costume every broadcast
    // wears had no call to action at all — a marketing mail that tells a member the rooms are open
    // and gives her nothing to tap. It is the shell's DEFAULT gold pill (never the welcome's plum
    // plate, which is her master's and belongs to that one mail), and the label and the destination
    // are declared once here so a hundred broadcasts cannot each invent their own way home.
    // >>> HER OWN BUTTON WINS, AND THE HOUSE'S IS THE FALLBACK (2026-08-21). <<< Asked of the
    // RESOLVED stack rather than the draft, so a button the normaliser dropped cannot quietly take
    // her way home with it.
    cta: broadcastHomeCta(content.blocks),
    footerHtml: unsubscribeFooterHtml(href),
  });
}
export function broadcastEmailText({ subject = "", body = "", blocks = null, unsubscribe = "" } = {}) {
  const href = String(unsubscribe || "");
  if (!href) return null;
  const content = emailContent({ emailBody: body, blocks });
  if (!content.words) return null;
  // E3: the text half through the same shell, so a broadcast's two parts cannot be shaped by two
  // different builders — which is exactly how the funnel's text part went out truncated mid-word.
  return houseEmailText({
    heading: String(subject || "").trim(),
    blocks: content.blocks,
    // >>> AND THE TEXT HALF WAS GIVEN NO CALL TO ACTION AT ALL, WHICH IS THE SAME FINDING FROM THE
    // OTHER SIDE (2026-08-21). <<< `houseEmailText` has always taken a `cta` and this caller never
    // passed one, so the HTML carried a way home and the plain-text half carried NONE — and a member
    // on a text-only client is exactly the one least likely to find her way back by guessing. E3's
    // own reason for one shell is that a broadcast's two parts may not be shaped by two different
    // builders; ONE law answers for both now (R28/R29).
    cta: broadcastHomeCta(content.blocks),
    footerText: unsubscribeFooterText(href),
  });
}

// ---------------------------------------------------------------------------
// DELIVERABILITY — HER "MEASURED FIRST" CLAUSE, AS ARITHMETIC RATHER THAN A PARAGRAPH.
//
// **THE GMAIL RAIL IS NOT A BULK RAIL AND THE NUMBERS SAY SO.** Google publishes two ceilings for
// mail sent through a Gmail mailbox: **500 recipients per day on a consumer gmail.com account** and
// **2,000 per day on a Google Workspace account**. Her house is **1,351 human members** today, and
// her conversion audience alone is **937**.
//
// So: on a consumer account a full-membership send is IMPOSSIBLE in a day (1,351 against 500) and
// even the never-paid list overruns it. On Workspace a full send fits — and spends 68% of the
// mailbox's entire daily allowance, leaving no room for a retry, a second send, or anything else
// that mailbox does that day.
//
// **THE PRODUCT REFUSES RATHER THAN TRUNCATING, WHICH IS HER "NEVER SILENTLY DEGRADE" CLAUSE READ
// LITERALLY.** A send that mails the first 500 and drops 851 is the worst outcome available: the
// ledger reads as a completed broadcast, 851 members were told nothing, and nobody finds out. The
// verdict below is computed BEFORE the confirmation is offered and is printed on the preview screen
// in plain numbers.
// ---------------------------------------------------------------------------
export const GMAIL_DAILY_CEILINGS = { consumer: 500, workspace: 2000 };
export const EMAIL_PROVIDER_PARKED = "Resend / SES — parked for her word, with the numbers, in DECISIONS.";

// >>> ITEM 3 (2026-08-13) — `emailCapacityVerdict`, `emailCapacitySentence` AND
// `EMAIL_TIGHT_FRACTION` ARE DELETED HERE AND LIVE IN `src/mailBudget.js` AS `mailSendVerdict` /
// `mailBudgetSentence`. THEY ARE REPLACED AT THEIR DECLARATION RATHER THAN OVERRIDDEN FROM ANOTHER
// FILE (DL-2/DT-2b/R181), because a retired capacity law sitting here beside its replacement is one
// import away from a round that finds it and asks the WRONG QUESTION with it. <<<
//
// **WHAT THE OLD ONE GOT WRONG IS NOT ITS ARITHMETIC, IT IS ITS DENOMINATOR.** It measured one
// broadcast list against the WHOLE MAILBOX — so it passed a 1,900-recipient list against a 2,000-a-day
// mailbox and left 100 sends for every welcome email, funnel reading and card reminder that day, and
// it passed two 1,000-recipient broadcasts independently because nothing counted the first one. The
// replacement measures a broadcast against **today's remaining BROADCAST allowance** (the ceiling
// minus her reserved 500, minus what broadcasts have already spent), which is her clause exactly.
// Its three verdicts survive unchanged and are still computed BEFORE the confirmation is offered:
// `fits`, `tight` (it fits and eats most of what is left — a fact she should have before she presses
// rather than after) and `over`. `unknown` survives as `unarmed` and still FAILS CLOSED.

// ---------------------------------------------------------------------------
// THE SEND — BATCHES, PROGRESS AND THE FOUR STATES A BROADCAST IS IN.
// ---------------------------------------------------------------------------
export const BROADCAST_STATES = ["queued", "sending", "done", "failed"];
export const broadcastStateValid = (s) => BROADCAST_STATES.includes(String(s || ""));

/** Batch sizes, per channel, because the two rails have nothing to do with each other. */
// C9's `sms` is batched at the EMAIL size rather than the push one, and the reason is the vendor
// rather than symmetry: a push goes to our own service and a text goes to a carrier through a
// metered API that answers per message. Ten with a pause behind it is the shape the mail rail was
// recalibrated to after a real send part-failed, and it is the conservative one to start from.
export const BATCH_SIZES = { push: 25, email: 10, sms: 10 };

// >>> **RECALIBRATED FROM A REAL SEND THAT PART-FAILED (2026-08-14), AND THE NUMBER IS THE VENDOR'S
// RATHER THAN A GUESS.** <<<
//
// Her first real broadcast — 1,128 people — reported **746 sent, 382 failed.** Traced against
// GMAIL'S OWN SENT FOLDER rather than against our counters (which record no reason at all), the
// failures are **INTERLEAVED THROUGH THE WHOLE SEND**: 6 in the first hundred, 79 in one middle
// hundred, 93 in another, with successes continuing to position 1,124. **gmail.com failed 353 times
// and DELIVERED 532 times in the same run** — so it is not the addresses, and it is not a quota wall
// either, because a wall is a tail and this has none.
//
// **THAT SHAPE IS A TOKEN BUCKET, AND THE ARITHMETIC SAYS THE SAME THING.** Ten sends fired
// CONCURRENTLY every 1,200ms is **8.3 messages a second**. The Gmail API meters a user at 250 quota
// units a second and `messages.send` costs 100 — a sustained ceiling of **2.5 a second**. We were
// running at more than three times what the mailbox will take, and roughly two thirds got through,
// which is what a bucket does when you overrun it.
//
// **5,000ms BETWEEN BATCHES OF TEN IS 2 A SECOND**, comfortably under that ceiling with room for the
// identity check that rides each send. **The cost is stated rather than hidden:** her 1,128-person
// send becomes about ten minutes instead of five, and the preview already tells her how long it will
// take. **A slower send that reaches everybody beats a fast one that reaches two thirds.**
export const EMAIL_BATCH_PAUSE_MS = 5000;

/** What the mailbox will take, per second, as a named fact so the pause above can be checked
 *  against it rather than believed. `messages.send` is 100 units of a 250-unit-per-second meter. */
export const GMAIL_SENDS_PER_SECOND = 2.5;

// ---------------------------------------------------------------------------
// >>> **HER CLAUSE 3: ONE SCARY NUMBER THAT IS REALLY TWO STORIES (2026-08-14).** <<<
//
// *"The progress line distinguishes failed (retrying) from failed (bad address) so she never again
// reads one scary number that is really two stories."*
//
// **THE TWO NEED OPPOSITE RECOVERIES, WHICH IS WHY THEY MAY NOT SHARE A COUNTER.** A throttled
// message is the SAME message a minute later and costs nothing but time; a bad address is never
// anything else and retrying it forever is how a sender's reputation goes. Reported as one number,
// *382 failed* reads as a catastrophe when 15 of it is a permanent, one-line fact and the rest is a
// pause.
// ---------------------------------------------------------------------------
export const SEND_FAILURE_KINDS = ["throttled", "bad-address", "unknown"];
export const failureRetryable = (kind) => String(kind || "") !== "bad-address";

/**
 * **WHICH KIND OF FAILURE THE MAILBOX JUST HANDED BACK.** Read off the STATUS first and the body
 * second, because a status code is the vendor's own verdict and a message is prose it can reword.
 *
 * **AN UNRECOGNISED FAILURE IS `unknown` AND `unknown` IS RETRYABLE**, which is the safe direction
 * here (R19): retrying a message that was never going to send costs one more attempt and a row that
 * says so, while marking a live member undeliverable on a failure nobody classified takes her off
 * every future send silently and forever.
 */
export function sendFailureClass({ status = 0, error = "" } = {}) {
  const s = Number(status) || 0;
  const text = String(error || "").toLowerCase();
  // 429 is the meter; 403 carries `rateLimitExceeded`/`userRateLimitExceeded`; 421 and 503 are the
  // mailbox asking for a slower hand. All of them mean *the same message, later*.
  if (s === 429 || s === 421 || s === 503 || /ratelimitexceeded|rate limit|too many|quota|backoff|try again later/.test(text)) {
    return "throttled";
  }
  // A permanent refusal ABOUT THE ADDRESS. Scoped to the address rather than to the word "invalid",
  // which appears in plenty of transient bodies.
  if (/invalid\s+(?:to|recipient|address)|recipient\s+(?:address\s+)?(?:rejected|not found)|no such user|address not found|mailbox unavailable|does not exist/.test(text)) {
    return "bad-address";
  }
  return "unknown";
}

/**
 * The house's own read of an address that can never work, for a failure nobody else classified.
 *
 * >>> **IT IS THE SIGNUP FIELD'S OWN LAW, NOT A SECOND SPELLING OF IT (R28/R29, 2026-08-14).** <<<
 * This shipped here as its own regex, and the signup round then needed the same question at the
 * door. Two answers to *is this an address* is two answers to whether a member can be reached at
 * all — and the one that drifted would be whichever nobody was looking at. **The consolidation
 * immediately paid for itself:** the field's law refuses the header-breaking characters this copy
 * did not, which is the one of the fifteen bad addresses this copy called sendable.
 */
export const addressLooksSendable = (email) => emailShapeValid(email);

export function batchPlan(total, size) {
  const n = Math.max(0, Number(total) || 0);
  const s = Math.max(1, Number(size) || 1);
  return { total: n, size: s, batches: Math.ceil(n / s) };
}

/**
 * **WHAT THE PROGRESS LINE SAYS, AND THE ONE WORD IT MAY NOT USE.** Her order asks for *sent,
 * delivered, failed*. **This house cannot say DELIVERED from a send call** — a push accepted by
 * Apple's gateway is not a push on a phone (that is what DT-1b's delivery beacon exists to
 * measure), and a mail accepted by Gmail is not a mail in an inbox. Reporting a transport
 * acceptance as a delivery is a false receipt (R141), so the counters are `sent` and `failed` and
 * the word DELIVERED appears nowhere on this surface.
 */
export const DELIVERY_NOT_CLAIMED = "Sent means the house handed it over. Whether it landed on a phone is what the delivery screen measures.";

export function progressLine(row = {}) {
  const state = String(row.state || "");
  const sent = Number(row.sent || 0);
  const failed = Number(row.failed || 0);
  const total = Number(row.total || 0);
  const channels = Array.isArray(row.channels) ? row.channels : [];

  // >>> **HER CLAUSE 3: THE FAILURE IS TWO STORIES AND IT SAYS SO (2026-08-14).** <<< `badAddresses`
  // is the permanent half; whatever is left of `failed` is the half a retry can still reach. The two
  // are named separately, and the retryable half says what she can DO about it — which is the whole
  // difference between a number that frightens her and a number she can act on.
  //
  // **A ROW OLDER THAN THIS ROUND CARRIES NO SPLIT AND FALLS BACK TO THE ONE SENTENCE IT ALWAYS
  // HAD** (R18/R68): an absent count is *not measured*, never *zero bad addresses*, so nothing
  // historical is re-labelled as clean by a column that did not exist when it ran.
  //
  // **AND A SEND WITH NO BAD ADDRESSES IN IT DOES NOT SAY SO.** *"…and 0 addresses that cannot
  // receive mail"* is a clause about a thing that did not happen, on a line she reads at a glance —
  // the split exists to tell two real piles apart, and a pile of nothing is not one of them.
  const knownBad = Number(row.badAddresses);
  const split = Number.isFinite(knownBad) && knownBad > 0 && failed > 0;
  const retryable = split ? Math.max(0, failed - knownBad) : 0;
  const bad = !failed
    ? ""
    : split
      ? `, ${retryable} to try again and ${knownBad} ${knownBad === 1 ? "address that cannot receive mail" : "addresses that cannot receive mail"}`
      : `, ${failed} could not be sent`;

  // >>> **A TWO-CHANNEL SEND HAS NO SINGLE FRACTION, AND PRINTING ONE READS AS NONSENSE ON THE
  // SURFACE SHE WATCHES FOR MINUTES (2026-08-14, found by the dress rehearsal).** <<<
  //
  // `sent` is `pushSent + emailSent` and `total` is PEOPLE, so a send on both channels reported
  // **"Sent to 5 of 3."** — measured, on her own screen, at the end of the rehearsal. The two rails
  // do not reach the same people (1,117 of her never-paid audience are mailable and 134 have a
  // device), so there is no honest denominator that covers both, and the repair is to stop implying
  // one rather than to invent a bigger number.
  //
  // **THE ROW ALREADY KNOWS EVERYTHING THIS NEEDS, so no migration and no second count** — what was
  // wrong was the sentence, not the data (R216's ruling one surface over: the LAW moves, not the
  // clause). One channel keeps the fraction it has always had, because there it is exact.
  const perChannel = channels.length > 1;
  const parts = perChannel
    ? [
        channels.includes("email") ? `${Number(row.emailSent || 0)} by email` : "",
        channels.includes("push") ? `${Number(row.pushSent || 0)} by push` : "",
      ].filter(Boolean).join(", ")
    : "";

  if (state === "queued") return `Queued for ${total} ${total === 1 ? "person" : "people"}.`;
  if (state === "sending") {
    return perChannel
      ? `Sending to ${total} ${total === 1 ? "person" : "people"}. ${parts} so far${bad}.`
      : `Sending. ${sent} of ${total} so far${bad}.`;
  }
  if (state === "failed") {
    return perChannel
      ? `Stopped part way. ${parts}${bad}.`
      : `Stopped after ${sent} of ${total}${bad}.`;
  }
  if (state === "done") {
    return perChannel
      ? `Sent to ${total} ${total === 1 ? "person" : "people"}: ${parts}${bad}.`
      : `Sent to ${sent} of ${total}${bad}.`;
  }
  return "";
}

/**
 * **THE LEDGER'S ACTION NAME, QUOTED RATHER THAN COMPARED AGAINST ITSELF.** `owner_actions` is
 * append-only at the database (migration 050's `DO INSTEAD NOTHING`), so a later drift in this
 * string splits her audit trail into two names with no error anywhere — which is the hole the money
 * sweep's own fixture opened at (a constant compared to itself is a tautology a blanked constant
 * walks straight through).
 */
export const BROADCAST_LEDGER_ACTION = "broadcast.send";

/** The four states of the owner's list, so a read that has not answered cannot say "none yet". */
export const BROADCAST_LIST_STATES = ["waiting", "failed", "empty", "list"];
export function broadcastListState({ rows = undefined, failed = false } = {}) {
  if (failed) return "failed";
  if (rows === undefined || rows === null) return "waiting";
  return rows.length ? "list" : "empty";
}
